import React, { useEffect, useState } from 'react';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import FormInput from '../../../shared/components/ui/FormInput';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import {
  getInventoryDailyTasks,
  updateInventoryDailyTasks,
  addReason,
  addScore,
  addAudit,
  getAllInventoryByProjectId,
  getTasksForAudit
} from '../../services/api/implementation/impl';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import Button from '../../../shared/components/ui/Button';
import Modal from '../../../shared/components/ui/Modal';
import toast from 'react-hot-toast';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import Loader from '../../../shared/components/ui/Loader';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import {
  isAddVendorsTaskType,
  isCreateInventoryTaskType,
  navigateForInventoryTaskType
} from '../../utils/inventoryTaskNavigation';

const DailyTasks = () => {
  const { type, date, projectID: navProjectID } = useLocation().state || {};
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const {
    register,
    formState: { errors },
    setValue,
    watch
  } = useForm();
  const { collapsed } = useSidebar();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectID = watch('project');
  const userID = Number(Cookies.get('userId'));

  // Get the date from navigation state, URL params, or default to today
  const [selectedDate, setSelectedDate] = useState(
    date || searchParams.get('date') || new Date().toISOString().split('T')[0]
  );

  // Modal states
  const [criticalityModal, setCriticalityModal] = useState(false);
  const [criticalityReason, setCriticalityReason] = useState('');
  const [criticalitySubmitting, setCriticalitySubmitting] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [scoreModal, setScoreModal] = useState(false);
  const [scoreValue, setScoreValue] = useState(null);
  const [scoreSubmitting, setScoreSubmitting] = useState(false);

  const [auditModal, setAuditModal] = useState(false);
  const [items, setItems] = useState([]);
  const [audits, setAudits] = useState([]);
  const [auditSubmitting, setAuditSubmitting] = useState(false);

  useEffect(() => {
    // Track page view
    mixpanel.track('Daily Tasks Page Viewed', {
      user_id: userID,
      timestamp: new Date().toISOString()
    });
  }, []);

  const {
    data: priorities,
    refetch: refetchPriorities,
    isLoading: isLoadingPriorities
  } = useQuery({
    queryKey: ['priorities', projectID, selectedDate],
    queryFn: () => {
      console.log('🔍 Calling API with:', { projectID, selectedDate, type });
      return getInventoryDailyTasks(projectID, selectedDate);
    },
    enabled: !!projectID && !!selectedDate,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false
  });

  const { data: itemsList, refetch: refetchItems } = useQuery({
    queryKey: ['items', projectID],
    queryFn: () => getAllInventoryByProjectId(projectID),
    enabled: !!projectID
  });

  useEffect(() => {
    if (itemsList) {
      setItems(itemsList);
    }
  }, [itemsList]);

  React.useEffect(() => {
    if (projects?.length) {
      const preferredId =
        navProjectID != null
          ? parseInt(navProjectID, 10)
          : projects[0]?.id;
      const valid =
        preferredId && projects.find((p) => p.id === preferredId)
          ? preferredId
          : projects[0]?.id;
      setValue('project', valid);
    }
  }, [projects, navProjectID, setValue]);

  React.useEffect(() => {
    if (type) {
      navigateForInventoryTaskType(navigate, type, {
        projectId: navProjectID || projectID,
        replace: true
      });
    }
  }, [type, navigate, navProjectID, projectID]);

  const getAllTasksForAudit = async (id) => {
    setAuditSubmitting(true);
    setAudits([]);
    try {
      const response = await getTasksForAudit(id, date);
      setAudits(response);
      setAuditModal(true);
    } catch (error) {
      console.error('Error getting tasks for audit:', error);
    } finally {
      setAuditSubmitting(false);
    }
  };

  // Modal handlers
  const handleCriticalityTask = (taskId, itemId) => {
    setSelectedTaskId(taskId);
    setSelectedItemId(itemId);
    setCriticalityModal(true);
    setCriticalityReason('');
  };

  const handleScoreTask = (taskId, userId) => {
    setSelectedTaskId(taskId);
    setSelectedUserId(userId);
    setScoreValue(null);
    setScoreModal(true);
  };

  // Mutation handlers
  const { mutate: handleCriticalitySubmit } = useMutation({
    mutationFn: async () => {
      if (!criticalityReason.trim()) {
        throw new Error('Please enter a reason');
      }
      const payload = {
        item_id: selectedItemId,
        reason: criticalityReason.trim()
      };
      const payload2 = { status: 'Completed' };

      await addReason(payload);
      return await updateInventoryDailyTasks(selectedTaskId, payload2);
    },
    onSuccess: () => {
      toast.success('Criticality task submitted successfully');
      setCriticalityModal(false);
      setCriticalityReason('');
      refetchPriorities();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to submit criticality task'
      );
    }
  });

  const { mutate: handleScoreSubmit } = useMutation({
    mutationFn: async () => {
      if (!scoreValue) {
        throw new Error('Please select a score');
      }

      const currentYear = new Date().getFullYear();
      // Calculate previous month again to ensure it's not null
      const currentMonth = new Date().getMonth(); // 0-11
      const previousMonth = currentMonth === 0 ? 12 : currentMonth;

      const payload = {
        performance: scoreValue,
        month: previousMonth, // Use calculated value instead of scoreMonth state
        year: currentYear
      };
      const payload2 = { status: 'Completed' };

      await addScore(payload);
      return await updateInventoryDailyTasks(selectedTaskId, payload2);
    },
    onSuccess: () => {
      toast.success('Score submitted successfully');
      setScoreModal(false);
      setScoreValue(null);
      refetchPriorities();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to submit score');
    }
  });

  const { mutate: handleAuditSubmit } = useMutation({
    mutationFn: async () => {
      // Filter audits that have both actual_stock and expired_stock values
      const validAudits = audits.filter(
        (audit) =>
          audit.actual_stock !== '' &&
          audit.actual_stock !== null &&
          audit.expired_stock !== '' &&
          audit.expired_stock !== null
      );

      if (validAudits.length === 0) {
        throw new Error('Please fill at least one item with audit and expired stock values');
      }

      const payload = {
        audits: validAudits.map((a) => ({
          id: a.id,
          actual_stock: Number(a.actual_stock),
          expired_stock: Number(a.expired_stock)
        }))
      };
      const payload2 = { status: 'Completed' };

      await addAudit(payload);
      return await updateInventoryDailyTasks(selectedTaskId, payload2);
    },
    onSuccess: () => {
      toast.success('Audit submitted successfully');
      setAuditModal(false);
      setAudits([]);
      refetchPriorities();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to submit audit');
    }
  });

  const handleAuditFieldChange = (idx, field, value) => {
    setAudits((prev) => prev.map((a, i) => (i === idx ? { ...a, [field]: value } : a)));
  };

  const handleAddAuditRow = () => {
    setAudits((prev) => [
      ...prev,
      { sku_id: null, actual_stock: '', expired_stock: '', reason: '' }
    ]);
  };

  const handleRemoveAuditRow = (idx) => {
    setAudits((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));
  };

  const processPrioritiesData = (data) => {
    if (!data || !Array.isArray(data)) return [];

    // Filter out completed tasks
    const pendingTasks = data.filter((item) => item.status !== 'Completed');

    return pendingTasks.map((task, index) => ({
      id: task.id,
      name: task.name,
      type: task.type,
      date: task.date,
      item_id: task.item_id,
      user_id: task.user_id,
      status: task.status,
      project_id: task.project_id,
      note: task.notes || '---'
    }));
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getDateStatus = (taskDate) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const dueDate = new Date(taskDate);
    dueDate.setHours(0, 0, 0, 0);

    const isSameDay = dueDate.getTime() === now.getTime();
    const isPast = dueDate < now;
    const isFuture = dueDate > now;

    if (isSameDay) {
      return { message: 'Today', bgColor: 'bg-yellow-100 text-yellow-800' };
    } else if (isPast) {
      const diffTime = now - dueDate;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return {
        message: `Delayed (${diffDays} ${diffDays === 1 ? 'day' : 'days'})`,
        bgColor: 'bg-red-100 text-red-800'
      };
    } else if (isFuture) {
      const diffTime = dueDate - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return {
        message: diffDays === 1 ? 'in 1 day' : `in ${diffDays} days`,
        bgColor: 'bg-green-100 text-green-800'
      };
    }

    return { message: '', bgColor: '' };
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    // Update URL with new date
    const newUrl = new URL(window.location);
    newUrl.searchParams.set('date', date);
    navigate(newUrl.pathname + newUrl.search, { replace: true });
  };

  const allTasks = processPrioritiesData(priorities);

  // Filter tasks by type if specified
  const tasks =
    type && type !== 'all'
      ? allTasks.filter((task) => task.type?.toLowerCase() === type?.toLowerCase())
      : allTasks;

  return (
    <Layout>
      <Loader isVisible={isProjectsLoading || isLoadingPriorities} />

      {/* Criticality Modal */}
      {criticalityModal && (
        <Modal
          isDirty
          confirmOnClose
          width={40}
          name="Criticality Task"
          onClose={() => setCriticalityModal(false)}>
          <div className="p-6">
            <h2 className="text-xl font-bold mb-4">Enter Reason</h2>
            <textarea
              className="w-full border border-gray-300 rounded-lg p-3 mb-4 min-h-[100px] resize-none"
              value={criticalityReason}
              onChange={(e) => setCriticalityReason(e.target.value)}
              placeholder="Enter reason for criticality"
            />
            <div className="flex justify-end gap-3">
              <Button type="button" onClick={() => setCriticalityModal(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => handleCriticalitySubmit()}
                disabled={criticalitySubmitting}>
                Submit
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Score Modal */}
      {scoreModal && (
        <Modal
          isDirty
          confirmOnClose
          width={50}
          name="Score Performance"
          onClose={() => setScoreModal(false)}>
          <div className="p-6">
            <h2 className="text-xl font-bold mb-4">Select Score (1-10)</h2>
            <div className="grid grid-cols-5 gap-3 mb-6">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                <button
                  key={num}
                  onClick={() => setScoreValue(num)}
                  className={`w-12 h-12 rounded-full border-2 font-bold text-lg ${
                    scoreValue === num
                      ? 'bg-blue-500 text-white border-blue-500'
                      : 'bg-gray-100 text-gray-700 border-gray-300'
                  }`}>
                  {num}
                </button>
              ))}
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" onClick={() => setScoreModal(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => handleScoreSubmit()}
                disabled={!scoreValue || scoreSubmitting}>
                Submit
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Audit Modal */}
      {auditModal && (
        <Modal
          isDirty
          confirmOnClose
          width={70}
          name={`Audit Items (${audits.length})`}
          onClose={() => {
            setAuditModal(false);
            setAudits([]);
          }}>
          <div className="relative">
            <div className="p-6 max-h-[70vh] overflow-y-auto relative">
              {audits.map((audit, idx) => {
                const physicalStock = audit.sku?.physicalStock || 0;
                const auditStock = audit.actual_stock || 0;
                const discrepancy = physicalStock - auditStock;

                return (
                  <div
                    key={idx}
                    className="border border-gray-200 rounded-lg p-4 mb-4 bg-white shadow-sm">
                    {/* First Row - Item Name and Discrepancy */}
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-bold text-lg text-gray-800">
                        {audit.sku?.item_name || `Item ${idx + 1}`}
                      </h3>
                      <div className="text-right">
                        <span className="text-sm text-gray-600">Discrepancy</span>
                        <div
                          className={`text-lg font-bold ${discrepancy !== 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {discrepancy}
                        </div>
                      </div>
                    </div>

                    {/* Second Row - Three Number Inputs */}
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          Physical Stock
                        </label>
                        <input
                          type="number"
                          className="w-full border border-gray-300 rounded-lg p-3 bg-gray-100"
                          value={physicalStock}
                          disabled
                          readOnly
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          Audit Stock
                        </label>
                        <input
                          type="number"
                          className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={audit.actual_stock || ''}
                          onChange={(e) =>
                            handleAuditFieldChange(idx, 'actual_stock', e.target.value)
                          }
                          placeholder="Enter audit stock"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          Expired Stock
                        </label>
                        <input
                          type="number"
                          className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={audit.expired_stock || ''}
                          onChange={(e) =>
                            handleAuditFieldChange(idx, 'expired_stock', e.target.value)
                          }
                          placeholder="Enter expired stock"
                        />
                      </div>
                    </div>

                    {/* Category and Unit Info */}
                    <div className="mt-3 text-sm text-gray-500">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded mr-2">
                        {audit.sku?.category_name || 'Category'}
                      </span>
                      <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded">
                        Unit: {audit.sku?.unit || 'N/A'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end gap-3 mt-6 absolute bottom-0 w-full bg-white pr-3 pt-1">
              <Button
                type="button"
                onClick={() => {
                  setAuditModal(false);
                  setAudits([]);
                }}>
                Cancel
              </Button>
              <Button type="button" onClick={() => handleAuditSubmit()} disabled={auditSubmitting}>
                Submit
              </Button>
            </div>
          </div>
        </Modal>
      )}

      <div
        className={`fixed top-0 z-10 transition-all duration-300 bg-white ${
          collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
        }`}>
        <Header
          data={{
            name: `${type === 'criticality' ? 'Criticality' : type === 'score' ? 'Score' : type === 'audit' ? 'Audit' : 'Daily'} Tasks`
          }}
        />
      </div>

      {/* Body Content */}
      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        <div className="pt-[5rem] pl-[1rem] pr-[1rem] relative mb-[3rem] rounded-t-lg flex flex-col items-center gap-4">
          <div className="overflow-scroll hide-scrollbar w-full relative rounded-t-lg">
            <table className="text-left w-full">
              <thead
                className="bg-gray-200 !text-gray-600 w-full"
                style={{ position: 'sticky', top: 0, left: 0, zIndex: 1 }}>
                <tr className="font-normal h-[3rem] w-full">
                  <th className="px-4 py-2 border-b font-normal">Task Name</th>
                  <th className="px-4 py-2 border-b font-normal">Type</th>
                  <th className="px-4 py-2 border-b font-normal">Due Date</th>
                  <th className="px-4 py-2 border-b font-normal">Notes</th>
                  <th className="px-4 py-2 border-b font-normal">Status</th>
                  <th className="px-4 py-2 border-b font-normal">Action</th>
                </tr>
              </thead>
              <tbody>
                {tasks && tasks.length > 0 ? (
                  tasks.map((task, index) => {
                    const dateStatus = getDateStatus(task.date);
                    return (
                      <React.Fragment key={task?.id || index}>
                        <tr className="border-b hover:bg-gray-50 bg-white h-[3.5rem]">
                          <td className="px-4 py-2">{task?.name || '---'}</td>
                          <td className="px-4 py-2">{task?.type || '---'}</td>
                          <td className="px-4 py-2">
                            <div>
                              <div className="text-sm">{formatDate(task?.date) || '---'}</div>
                              {dateStatus.message && (
                                <div className="text-xs text-gray-500">{dateStatus.message}</div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-2">{task?.note || '---'}</td>
                          <td className="px-4 py-2">
                            <div className="relative z-0">
                              <span className="flex pl-6 overflow-hidden text-sm">
                                {task?.status || '---'}
                              </span>
                              <div
                                className={`w-[5px] h-[100%] rounded-t-2xl rounded-r-[3rem] absolute left-[4px] top-[-2px] ${
                                  task?.status === 'Completed'
                                    ? 'bg-[#3F8D78]'
                                    : task?.status === 'Pending'
                                      ? 'bg-[#F59E0B]'
                                      : task?.status === 'In Progress'
                                        ? 'bg-[#3B82F6]'
                                        : task?.status === 'Delayed'
                                          ? 'bg-[#C2281B]'
                                          : 'bg-gray-400'
                                }`}>
                                &nbsp;
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center justify-center w-full relative gap-2">
                              <button
                                onClick={() => {
                                  if (
                                    navigateForInventoryTaskType(navigate, task.type, {
                                      projectId: task.project_id || projectID
                                    })
                                  ) {
                                    return;
                                  }
                                  if (task.type?.toLowerCase() === 'criticality') {
                                    handleCriticalityTask(task.id, task.item_id);
                                  } else if (
                                    task.type?.toLowerCase() === 'score' ||
                                    task.type?.toLowerCase() === 'scoring'
                                  ) {
                                    handleScoreTask(task.id, task.user_id);
                                  } else if (
                                    task.type?.toLowerCase() === 'audit' ||
                                    task.type?.toLowerCase() === 'auditing'
                                  ) {
                                    getAllTasksForAudit(task.project_id);
                                    setSelectedTaskId(task.id);
                                  }
                                }}
                                className="text-white px-3 py-1 rounded-md text-xs font-medium"
                                style={{ backgroundColor: '#2a565e' }}>
                                {isCreateInventoryTaskType(task.type)
                                  ? 'Open Stock'
                                  : isAddVendorsTaskType(task.type)
                                    ? 'Add Vendors'
                                    : 'Update'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center py-10 align-middle">
                      <div className="flex flex-col items-center justify-center w-full h-full">
                        <div className="text-gray-400 text-4xl mb-2">📋</div>
                        <h2 className="text-lg font-bold text-gray-800 mb-1">No Data Found</h2>
                        <p className="text-gray-600 text-sm">
                          No tasks available for this date.
                          <br />
                          Check back later for new tasks.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DailyTasks;
